import React, { useState } from 'react';
import { Phone, Mail, X } from 'lucide-react';
import emailjs from '@emailjs/browser';

const BookingPopup = ({ onClose }) => {
    const [isOpen] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
    });

    const [errors, setErrors] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const [touched, setTouched] = useState({
        name: false,
        email: false,
        phone: false,
        message: false
    });

    // Validation functions
    const validateName = (name) => {
        if (!name.trim()) {
            return 'Name is required';
        }
        if (name.trim().length < 2) {
            return 'Name must be at least 2 characters';
        }
        if (!/^[a-zA-Z\s]*$/.test(name)) {
            return 'Name can only contain letters and spaces';
        }
        return '';
    };

    const validateEmail = (email) => {
        if (!email.trim()) {
            return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address';
        }
        return '';
    };

    const validatePhone = (phone) => {
        if (!phone.trim()) {
            return 'Phone number is required';
        }
        // Allow Indian phone numbers (10 digits, optionally starting with +91 or 0)
        const phoneRegex = /^(?:(?:\+|0{0,2})91(\s*[-]\s*)?|[0]?)?[6789]\d{9}$/;
        if (!phoneRegex.test(phone)) {
            return 'Please enter a valid Indian phone number';
        }
        return '';
    };

    const validateMessage = (message) => {
        if (!message.trim()) {
            return 'Message is required';
        }
        if (message.trim().length < 10) {
            return 'Message must be at least 10 characters';
        }
        return '';
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        
        let error = '';
        switch (name) {
            case 'name':
                error = validateName(value);
                break;
            case 'email':
                error = validateEmail(value);
                break;
            case 'phone':
                error = validatePhone(value);
                break;
            case 'message':
                error = validateMessage(value);
                break;
            default:
                break;
        }
        
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // If field has been touched, validate on change
        if (touched[name]) {
            handleBlur(e);
        }
    };

    const validateForm = () => {
        const newErrors = {
            name: validateName(formData.name),
            email: validateEmail(formData.email),
            phone: validatePhone(formData.phone),
            message: validateMessage(formData.message)
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== '');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Mark all fields as touched
        setTouched({
            name: true,
            email: true,
            phone: true,
            message: true
        });

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const templateParams = {
                to_email: 'sudutta@gmail.com',
                from_name: formData.name,
                from_email: formData.email,
                phone: formData.phone,
                message: formData.message,
            };

            await emailjs.send(
                'service_km0i2ak',
                'template_28zpadg',
                templateParams,
                '623kwrJgAFBG_e5QS'
            );

            setSubmitStatus('success');
            setTimeout(() => {
                onClose();
            }, 2000);
            
        } catch (error) {
            console.error('Error sending email:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getInputClassName = (fieldName) => {
        const baseClasses = "w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent";
        if (!touched[fieldName]) return `${baseClasses} border-gray-300`;
        return `${baseClasses} ${
            errors[fieldName] 
                ? 'border-red-500 bg-red-50' 
                : 'border-green-500 bg-green-50'
        }`;
    };
  
    return (
      <>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
              onClick={onClose}
            />
            <div className="relative bg-white rounded-lg w-full max-w-md p-6 shadow-xl">
              <button 
                onClick={onClose}
                className="absolute top-3 right-2 text-teal-700 hover:text-gray-600 transition-colors mb-2"
              >
                <X className="h-6 w-6" />
              </button>
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Book Your Career Counselling Session
                </h3>
                <p className="text-gray-600">
                  Take the first step towards a brighter future. Schedule your session today.
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
                  Thank you for your submission! We'll contact you shortly.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
                  Sorry, there was an error submitting your form. Please try again or contact us directly.
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 mb-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={getInputClassName('name')}
                    required
                  />
                  {touched.name && errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={getInputClassName('email')}
                    required
                  />
                  {touched.email && errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Your Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={getInputClassName('phone')}
                    required
                  />
                  {touched.phone && errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
                <div>
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`${getInputClassName('message')} h-24`}
                  />
                  {touched.message && errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full p-4 bg-teal-600 text-white rounded-lg transition-colors ${
                    isSubmitting 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-teal-700'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>

              <div className="grid grid-cols-2 gap-4">
                <a href="mailto:sudutta@gmail.com" className="flex items-center justify-center gap-2 p-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
                  <Mail className="h-5 w-5" />
                  <span>Email Us</span>
                </a>
                <a href="tel:9731166822" className="flex items-center justify-center gap-2 p-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
                  <Phone className="h-5 w-5" />
                  <span>Call Now</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </>
    );
};

export default BookingPopup;